import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import MBTGraph from "./MBTGraph";
import Spinner from "./ui/spinner";
import Button from "./ui/button";
import './GlobalTestsGraph.css';

import { extractStepsByType } from "../utils/testUtils";

const GlobalTestsGraph = ({ testScenarios, nodes }) => {
  const { t } = useTranslation();
  
  // Always use a defined array to avoid forEach/map errors
  const safeTestScenarios = Array.isArray(testScenarios) ? testScenarios : [];
  const [updatedScenarios, setUpdatedScenarios] = useState(safeTestScenarios);
  const [filterByNode, setFilterByNode] = useState("");
  const [updateStatus, setUpdateStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // State to manage accordion open/close
  const [isGraphExpanded, setIsGraphExpanded] = useState(false);

  useEffect(() => {
    setUpdatedScenarios(Array.isArray(testScenarios) ? testScenarios : []);
  }, [testScenarios]);

  const filteredScenarios = useMemo(() => {
    if (!filterByNode) return updatedScenarios;
    return updatedScenarios.filter(
      (scenario) => scenario.nodeName === filterByNode
    );
  }, [updatedScenarios, filterByNode]);

  const scenariosByNode = useMemo(() => {
    const grouped = {};
    updatedScenarios.forEach((scenario) => {
      const nodeName = scenario.nodeName || t('globalTestsGraph.notAssociated');
      if (!grouped[nodeName]) grouped[nodeName] = [];
      grouped[nodeName].push(scenario);
    });
    return grouped;
  }, [updatedScenarios, t]);

  const adaptedScenarios = filteredScenarios.map((scenario) => {
    const stepsFromDescription = extractStepsByType(scenario);
    return {
      title: scenario.title,
      type: scenario.type || "acceptance",
      description: scenario.description,
      steps:
        scenario.steps ||
        (stepsFromDescription.length > 0 ? stepsFromDescription[0] : []),
      tags: scenario.tags || [],
      priority: scenario.priority || "medium",
      code: scenario.code,
      id: scenario.id,
      additionalDescription: scenario.additionalDescription,
      nodeName: scenario.nodeName,
    };
  });

  // ...copy the handleScenariosChange function here...

  // Auto-expand if there are scenarios
  useEffect(() => {
    if (safeTestScenarios.length > 0) {
      setIsGraphExpanded(true);
    } else {
      setIsGraphExpanded(false);
    }
  }, [safeTestScenarios.length]);

  return (
    <div className="global-tests-graph-section">
      {/* Modern header - Accordion */}
      <button 
        className={`graph-header ${isGraphExpanded ? 'expanded' : ''}`}
        onClick={() => setIsGraphExpanded(!isGraphExpanded)}
      >
        <div className="graph-title-area">
          <span className="graph-title-icon">🔄</span>
          <div className="graph-title-text">
            <h3>{t('globalTestsGraph.dmbtView')}</h3>
            <p className="graph-subtitle">
              {t('globalTestsGraph.subtitle')}
              {safeTestScenarios.length > 0 && ` (${safeTestScenarios.length} ${t('globalTestsGraph.test', { count: safeTestScenarios.length })})`}
            </p>
          </div>
        </div>
        
        <div className="graph-status-area">
          {updateStatus && (
            <div className={`status-badge-graph ${updateStatus.type}`}>
              {updateStatus.message}
            </div>
          )}
          {loading && (
            <div className="loading-indicator">
              <Spinner size="sm" />
              <span>{t('app.loading')}</span>
            </div>
          )}
          <span className={`expand-arrow ${isGraphExpanded ? 'rotated' : ''}`}>
            ▼
          </span>
        </div>
      </button>

      {/* Accordion content */}
      <div className={`graph-accordion-content ${isGraphExpanded ? 'expanded' : ''}`}>
        {/* Modernized control bar */}
        <div className="graph-controls">
          <div className="control-group">
            <label className="control-label">{t('globalTestsGraph.filterByNode')}:</label>
            <select
              value={filterByNode}
              onChange={(e) => setFilterByNode(e.target.value)}
              className="control-select"
            >
              <option value="">{t('globalTestsGraph.allNodes')}</option>
              {Object.keys(scenariosByNode)
                .sort()
                .map((nodeName) => (
                  <option key={nodeName} value={nodeName}>
                    {nodeName} ({scenariosByNode[nodeName].length})
                  </option>
                ))}
            </select>
          </div>
        </div>

        {/* Corps du graphique */}
        <div className="graph-content">
          {safeTestScenarios.length > 0 ? (
            <MBTGraph
              scenarios={adaptedScenarios}
              onScenariosChange={() => {}} // à compléter avec la logique de mise à jour
            />
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">📊</div>
              <h4 className="empty-state-title">{t('globalTestsGraph.noTestsLoaded')}</h4>
              <p className="empty-state-description">
                {t('globalTestsGraph.loadTestsInstruction')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalTestsGraph;